import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Footer } from "../components/landing/Footer";
import { Header } from "../components/landing/Header";
import { getGuideAlternates } from "../data/guideAlternates";
import { guideContentByPath } from "../data/guideContent";
import { getGuideSeo } from "../data/guideSeo";
import { trackEvent } from "../lib/analytics";

const languageLabels = {
  en: "English",
  "pt-BR": "Português",
  es: "Español",
};

export function GuidePage() {
  const { pathname } = useLocation();
  const guide =
    guideContentByPath[pathname] ?? guideContentByPath[`${pathname}/`];

  useEffect(() => {
    if (!guide) return;
    const guideSeo = getGuideSeo(guide.path);

    trackEvent("stalkie_guide_view", {
      guide_path: guide.path,
      guide_locale: guide.locale,
    });

    const canonicalUrl = `https://stalkie.app${guide.path}`;
    const setMeta = (selector: string, value: string) => {
      document.head.querySelector(selector)?.setAttribute("content", value);
    };

    document.title = `${guide.title} - Stalkie`;
    document.documentElement.lang =
      guide.locale === "pt-BR" ? "pt-BR" : guide.locale;
    setMeta('meta[name="description"]', guide.description);
    setMeta('meta[property="og:title"]', guide.title);
    setMeta('meta[property="og:description"]', guide.description);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta(
      'meta[property="og:locale"]',
      guide.locale === "pt-BR"
        ? "pt_BR"
        : guide.locale === "es"
          ? "es_ES"
          : "en_US",
    );
    setMeta('meta[name="twitter:title"]', guide.title);
    setMeta('meta[name="twitter:description"]', guide.description);

    const canonical = document.head.querySelector('link[rel="canonical"]');
    canonical?.setAttribute("href", canonicalUrl);

    document.head
      .querySelectorAll('link[rel="alternate"]')
      .forEach((element) => element.remove());
    const alternates = getGuideAlternates(guide.path);
    Object.entries(alternates ?? {}).forEach(([locale, path]) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = locale === "pt-BR" ? "pt-BR" : locale;
      link.href = `https://stalkie.app${path}`;
      link.dataset.guideHreflang = "true";
      document.head.appendChild(link);
    });
    if (alternates) {
      const defaultLink = document.createElement("link");
      defaultLink.rel = "alternate";
      defaultLink.hreflang = "x-default";
      defaultLink.href = `https://stalkie.app${alternates.en}`;
      defaultLink.dataset.guideHreflang = "true";
      document.head.appendChild(defaultLink);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: guide.title,
      description: guide.description,
      mainEntityOfPage: canonicalUrl,
      inLanguage: guide.locale,
      publisher: {
        "@type": "Organization",
        name: "Stalkie",
        url: "https://stalkie.app/",
      },
      ...(guideSeo.dateModified ? { dateModified: guideSeo.dateModified } : {}),
      author: {
        "@type": "Organization",
        name: "Stalkie",
        url: "https://stalkie.app/",
      },
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: guide.faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    };
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name:
            guide.locale === "pt-BR"
              ? "Início"
              : guide.locale === "es"
                ? "Inicio"
                : "Home",
          item: "https://stalkie.app/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: guide.title,
          item: canonicalUrl,
        },
      ],
    };
    [
      ["stalkie-guide-schema", schema],
      ["stalkie-guide-faq-schema", faqSchema],
      ["stalkie-guide-breadcrumb-schema", breadcrumbSchema],
    ].forEach(([id, value]) => {
      let script = document.getElementById(
        id as string,
      ) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = id as string;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(value);
    });
  }, [guide]);

  if (!guide) return null;
  const guideSeo = getGuideSeo(guide.path);

  const homeLabel =
    guide.locale === "pt-BR"
      ? "Início"
      : guide.locale === "es"
        ? "Inicio"
        : "Home";
  const ctaLabel =
    guide.locale === "pt-BR"
      ? "Pesquisar um perfil público"
      : guide.locale === "es"
        ? "Buscar un perfil público"
        : "Search a public profile";
  const faqHeading =
    guide.locale === "pt-BR"
      ? "Perguntas frequentes"
      : guide.locale === "es"
        ? "Preguntas frecuentes"
        : "Frequently asked questions";
  const relatedLabel =
    guide.locale === "pt-BR"
      ? "Mais guias"
      : guide.locale === "es"
        ? "Más guías"
        : "More guides";
  const relatedLinks =
    guide.locale === "pt-BR"
      ? [
          [
            "Como ver quem alguém seguiu",
            "/pt/como-ver-quem-alguem-seguiu-no-instagram/",
          ],
          [
            "Rastreador de seguidores",
            "/pt/rastreador-de-seguidores-instagram/",
          ],
          ["Rastreador anônimo", "/pt/rastreador-anonimo-instagram/"],
        ]
      : guide.locale === "es"
        ? [
            [
              "Cómo ver a quién alguien siguió",
              "/es/como-ver-a-quien-alguien-siguio-en-instagram/",
            ],
            [
              "Rastreador de seguidores",
              "/es/rastreador-de-seguidores-instagram/",
            ],
            ["Rastreador anónimo", "/es/rastreador-anonimo-instagram/"],
          ]
        : [
            [
              "How to see who someone followed",
              "/how-to-see-who-someone-followed-on-instagram/",
            ],
            ["Instagram follow tracker", "/instagram-follow-tracker/"],
            ["Anonymous Instagram tracker", "/anonymous-instagram-tracker/"],
          ];

  return (
    <div className="st-page st-guide-page" id="top">
      <Header />
      <main>
        <article className="st-guide-article">
          <div className="st-container st-guide-breadcrumb">
            <Link to="/">{homeLabel}</Link>
            <span aria-hidden="true">/</span>
            <span>{guide.eyebrow}</span>
          </div>
          <header className="st-container st-guide-hero">
            <p className="st-eyebrow">{guide.eyebrow}</p>
            <h1>{guide.title}</h1>
            {guideSeo.answer && (
              <p className="st-guide-answer">{guideSeo.answer}</p>
            )}
            <p className="st-guide-intro">{guide.intro}</p>
            {guideSeo.dateModified && (
              <time
                className="st-guide-updated"
                dateTime={guideSeo.dateModified}
              >
                {guide.locale === "pt-BR"
                  ? `Atualizado em ${guideSeo.dateModified}`
                  : guide.locale === "es"
                    ? `Actualizado el ${guideSeo.dateModified}`
                    : `Updated ${guideSeo.dateModified}`}
              </time>
            )}
          </header>
          <div className="st-container st-guide-layout">
            <div className="st-guide-content">
              {guide.sections.map((section) => (
                <section key={section.heading}>
                  <h2>{section.heading}</h2>
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {section.bullets && (
                    <ul>
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
              <section>
                <h2>{faqHeading}</h2>
                <div className="st-guide-faq">
                  {guide.faq.map((item) => (
                    <details key={item.question}>
                      <summary>{item.question}</summary>
                      <p>{item.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            </div>
            <aside className="st-guide-aside">
              <strong>{relatedLabel}</strong>
              {relatedLinks.map(([label, path]) => (
                <Link key={path} to={path}>
                  {label}
                </Link>
              ))}
            </aside>
          </div>
          <div className="st-container st-guide-cta">
            <Link
              to="/"
              className="st-header-cta"
              onClick={() =>
                trackEvent("stalkie_guide_cta_clicked", {
                  guide_path: guide.path,
                  guide_locale: guide.locale,
                })
              }
            >
              {ctaLabel}
            </Link>
            <span>{languageLabels[guide.locale]}</span>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
